import { useState, useEffect } from "react";
import {
    Bar,
    BarChart,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    LineChart,
    Line,
    PieChart,
    Pie,
    type PieProps,
    ResponsiveContainer
} from "recharts";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import trdatacsv from "./tricsmin.json";
import "./trics.css";
import { useNavigate } from "react-router-dom";
//import "./trics-bootstrap.css";

// Single half-hour of person user breakdown (bar chart)
export interface PersonUserdata {
    HalfHour: string;     // e.g. "0700-0730"
    Live: number;
    Servicing: number;
    Visitor: number;
    Work: number;
    WorkLive: number;
}

// Mode choice flow data for a time period (line chart)
export interface ModeChoicedata {
    HalfHour: string;     // e.g. "0700-0730"
    CARS: number;         // CA
    PEDNS: number;        // PN
    PEDCYC: number;       // PC
    TAXIS: number;        // TX
    MOTCYC: number;       // MC
    BUSPAS: number;       // BP
    LGVS: number;         // LG
    OGVS: number;         // OS
}

// Person trips per development (bar chart)
export interface PersonDeveldata {
    HalfHour: string;     // e.g. "0700-0730"
    Church: number;
    Community: number;
    Fitness: number;
    JustforPets: number;
    Library: number;
    Morrisons: number;
    PoundWorld: number;
}

// Generic chart data for pie charts
export interface ChartData {
    name: string;
    value: number;
    perctext?: string;    // percentage label for pie chart
}

// Raw trics data row, from your json/csv
export interface tricsdata {
    DV: string;   // Development code
    OR: string;   // Origin code
    US: string;   // User code
    DN: string;   // Direction code
    TM: string;   // Time code (e.g. "70" for 07:00)
    CA: string;   // Cars
    TX: string;   // Taxis
    MC: string;   // Motorcycles
    LG: string;   // LGVs
    PS: string;   // PSV (not used above, but present)
    O1: string;
    O2: string;
    OS: string;   // OGVS
    TV: string;
    V1: string;   // Vehicle occupancy bands 1-6
    V2: string;
    V3: string;
    V4: string;
    V5: string;
    V6: string;
    V7: string;
    TO: string;
    PC: string;   // Pedal cycles
    PN: string;   // Pedestrians
    BP: string;   // Bus passengers
    TR: string;
    CP: string;
    RP: string;
    TP: string;   // Person trips
}


const DevInitToggVal = [0, 1, 2, 3, 4, 5, 6];
const UserInitToggVal = [0, 1, 2, 3, 4];
const DirnInitToggVal = [0, 1];
const OrigInitToggVal = [0, 1];
const TimeInitToggVal = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
    13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23
];
const ArrDevCodes = ["C", "Y", "F", "J", "L", "M", "P"];
const ArrUserCodes = ["L", "S", "V", "W", "K"];
const ArrDirnCodes = ["I", "O"];
const ArrOrigCodes = ["E", "I"];
const ArrTimeCodes = [
    ",70", ",73", ",80", ",83", ",90", ",93", ",100", ",103", ",110", ",113",
    ",120", ",123", ",130", ",133", ",140", ",143", ",150", ",153", ",160", ",163",
    ",170", ",173", ",180", ",183"
];

const COLORS = [
    "#0088FE", "#FF8C00", "#00C49F", "#E32636",
    "#8a2be2", "#0048BA", "#8884d8", "#a2ca9d"
];

type PieLabelRenderer = Extract<NonNullable<PieProps["label"]>, (...args: any[]) => unknown>;

const renderPieLabel: PieLabelRenderer = ({ payload }) => {
    if (
        payload &&
        typeof payload === "object" &&
        "perctext" in payload &&
        typeof payload.perctext === "string"
    ) {
        return payload.perctext;
    }

    return "";
};

// ---- Pure data function ----
function parseTricsData(
    trdata: tricsdata[],
    devTogg: number[],
    userTogg: number[],
    dirnTogg: number[],
    origTogg: number[],
    timeTogg: number[]
) {
    // Map selected toggle indices to code strings
    const devCodes = devTogg.map(i => ArrDevCodes[i]);
    const userCodes = userTogg.map(i => ArrUserCodes[i]);
    const dirnCodes = dirnTogg.map(i => ArrDirnCodes[i]);
    const origCodes = origTogg.map(i => ArrOrigCodes[i]);
    const timeCodes = timeTogg.map(i => ArrTimeCodes[i].slice(1)); // Remove leading ',' for matching TM

    // Helpers for initializing chart arrays
    const buildHalfHourLabels = () => {
        const arr: string[] = [];
        for (let ix = 7; ix < 19; ix++) {
            let start1 = String(ix).padStart(2, '0') + "00";
            let end1 = String(ix).padStart(2, '0') + "30";
            arr.push(`${start1.slice(0, 2)}${start1.slice(2)}-${end1.slice(0, 2)}${end1.slice(2)}`);
            let start2 = String(ix).padStart(2, '0') + "30";
            let end2 = String(ix + 1).padStart(2, '0') + "00";
            arr.push(`${start2.slice(0, 2)}${start2.slice(2)}-${end2.slice(0, 2)}${end2.slice(2)}`);
        }
        return arr;
    };
    const halfHours = buildHalfHourLabels();

    // Chart arrays initialization
    const personUser: PersonUserdata[] = halfHours.map(hh => ({
        HalfHour: hh,
        Live: 0, Servicing: 0, Visitor: 0, Work: 0, WorkLive: 0
    }));
    const personDevel: PersonDeveldata[] = halfHours.map(hh => ({
        HalfHour: hh,
        Church: 0, Community: 0, Fitness: 0, JustforPets: 0, Library: 0, Morrisons: 0, PoundWorld: 0
    }));
    const modeChoice: ModeChoicedata[] = halfHours.map(hh => ({
        HalfHour: hh,
        CARS: 0, PEDNS: 0, PEDCYC: 0, TAXIS: 0, MOTCYC: 0, BUSPAS: 0, LGVS: 0, OGVS: 0
    }));

    // Pie data
    let vehOccupTotals = [0, 0, 0, 0, 0, 0];
    let modeSplitTotals = [0, 0, 0, 0, 0, 0, 0, 0]; // CARS, PEDNS, PEDCYC, TAXIS, MOTCYC, BUSPAS, LGVS, OGVS

    // Filtering and aggregating
    for (const row of trdata) {
        // Filter logic
        if (!devCodes.includes(row.DV)) continue;
        if (!userCodes.includes(row.US)) continue;
        if (!dirnCodes.includes(row.DN)) continue;
        if (!origCodes.includes(row.OR)) continue;
        if (!timeCodes.includes(row.TM)) continue;

        // HalfHour index
        let nIndex = (() => {
            let s = row.TM.padStart(3, '0'); // e.g. "073"
            let h = parseInt(s.slice(0, 2));
            let m = s[2] === "3" ? 1 : 0;
            return 2 * (h - 7) + m;
        })();

        // Safety: skip if outside our range
        if (nIndex < 0 || nIndex >= halfHours.length) continue;

        // PersonUserdata
        switch (row.US) {
            case "L": personUser[nIndex].Live += parseInt(row.TP || "0") || 0; break;
            case "S": personUser[nIndex].Servicing += parseInt(row.TP || "0") || 0; break;
            case "V": personUser[nIndex].Visitor += parseInt(row.TP || "0") || 0; break;
            case "W": personUser[nIndex].Work += parseInt(row.TP || "0") || 0; break;
            case "K": personUser[nIndex].WorkLive += parseInt(row.TP || "0") || 0; break;
        }
        // PersonDeveldata
        switch (row.DV) {
            case "C": personDevel[nIndex].Church += parseInt(row.TP || "0") || 0; break;
            case "Y": personDevel[nIndex].Community += parseInt(row.TP || "0") || 0; break;
            case "F": personDevel[nIndex].Fitness += parseInt(row.TP || "0") || 0; break;
            case "J": personDevel[nIndex].JustforPets += parseInt(row.TP || "0") || 0; break;
            case "L": personDevel[nIndex].Library += parseInt(row.TP || "0") || 0; break;
            case "M": personDevel[nIndex].Morrisons += parseInt(row.TP || "0") || 0; break;
            case "P": personDevel[nIndex].PoundWorld += parseInt(row.TP || "0") || 0; break;
        }
        // ModeChoiceData
        modeChoice[nIndex].CARS += parseInt(row.CA || "0") || 0;
        modeChoice[nIndex].PEDNS += parseInt(row.PN || "0") || 0;
        modeChoice[nIndex].PEDCYC += parseInt(row.PC || "0") || 0;
        modeChoice[nIndex].TAXIS += parseInt(row.TX || "0") || 0;
        modeChoice[nIndex].MOTCYC += parseInt(row.MC || "0") || 0;
        modeChoice[nIndex].BUSPAS += parseInt(row.BP || "0") || 0;
        modeChoice[nIndex].LGVS += parseInt(row.LG || "0") || 0;
        modeChoice[nIndex].OGVS += parseInt(row.OS || "0") || 0;

        // Pie data aggregators
        vehOccupTotals[0] += parseInt(row.V1 || "0") || 0;
        vehOccupTotals[1] += parseInt(row.V2 || "0") || 0;
        vehOccupTotals[2] += parseInt(row.V3 || "0") || 0;
        vehOccupTotals[3] += parseInt(row.V4 || "0") || 0;
        vehOccupTotals[4] += parseInt(row.V5 || "0") || 0;
        vehOccupTotals[5] += parseInt(row.V6 || "0") || 0;

        modeSplitTotals[0] += parseInt(row.CA || "0") || 0;
        modeSplitTotals[1] += parseInt(row.PN || "0") || 0;
        modeSplitTotals[2] += parseInt(row.PC || "0") || 0;
        modeSplitTotals[3] += parseInt(row.TX || "0") || 0;
        modeSplitTotals[4] += parseInt(row.MC || "0") || 0;
        modeSplitTotals[5] += parseInt(row.BP || "0") || 0;
        modeSplitTotals[6] += parseInt(row.LG || "0") || 0;
        modeSplitTotals[7] += parseInt(row.OS || "0") || 0;
    }

    // Pie chart: Mode Split For Selected Time
    const totalModes = modeSplitTotals.reduce((a, b) => a + b, 0);
    const modeSplitLabels = ['CARS', 'PED ', 'CYCLE', 'TAXIS', 'MOTORBIKE', 'BUS PAS', 'LGVS', 'OGVS'];
    const modeSplitPie: ChartData[] = modeSplitTotals.map((val, i) => ({
        name: modeSplitLabels[i],
        value: val,
        perctext: totalModes > 0 ? Math.max(1, Math.round((val * 100) / totalModes)) + '%' : '0%'
    }));

    // Pie chart: Vehicle Occupancy For Selected Time
    const totalOccup = vehOccupTotals.reduce((a, b) => a + b, 0);
    const vehOccupPie: ChartData[] = vehOccupTotals.map((val, i) => ({
        name: `Veh Occup ${i + 1}`,
        value: val,
        perctext: totalOccup > 0 ? Math.max(1, Math.round((val * 100) / totalOccup)) + '%' : '0%'
    }));

    return {
        personUser,
        personDevel,
        modeChoice,
        vehOccupPie,
        modeSplitPie
    };
}


// ---- Chart Components ----
const CustomizedAxisTick = (props: any) => {
    const { x, y, payload } = props;
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={16}
                fontSize={12}
                textAnchor="end"
                fill="#666"
                transform="rotate(-35)"
            >
                {payload.value}
            </text>
        </g>
    );
};

const PersonByUserChart = ({ data }: { data: PersonUserdata[] }) => (
    <>
        <h5 className="trchartheader">Person Trips by User Type</h5>
        <BarChart width={670} height={270} data={data}
            margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="HalfHour" height={60} interval={0} tickCount={24} tick={<CustomizedAxisTick />} />
            <YAxis tick={{ fontSize: 13 }} />
            <Tooltip />
            <Legend layout="vertical" verticalAlign="top" align="right" />
            <Bar dataKey="Live" stackId="a" fill="#00ace6" maxBarSize={9} />
            <Bar dataKey="Servicing" stackId="a" fill="#cc3300" maxBarSize={9} />
            <Bar dataKey="Visitor" stackId="a" fill="#8a2be2" maxBarSize={9} />
            <Bar dataKey="Work" stackId="a" fill="#ff8c1a" maxBarSize={9} />
            <Bar dataKey="WorkLive" name="Work Live" stackId="a" fill="#85ba8a" maxBarSize={9} />
        </BarChart>
    </>
);

const PersonTripsPerDevelopment = ({ data }) => (
    <>
        <h5 className="trchartheader">Person Trips Per Development</h5>
        <BarChart width={670} height={370} data={data}
            margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="HalfHour" height={60} interval={0} tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 13 }} />
            <Tooltip />
            <Legend layout="vertical" verticalAlign="top" align="right" />
            <Bar dataKey="Church" name="Church" stackId="a" fill="#00ace6" maxBarSize={9} />
            <Bar dataKey="Community" name="Community Centre" stackId="a" fill="#cc3300" maxBarSize={9} />
            <Bar dataKey="Fitness" name="Fitness Centre" stackId="a" fill="#8a2be2" maxBarSize={9} />
            <Bar dataKey="JustforPets" name="Just for Pets" stackId="a" fill="#ff8c1a" maxBarSize={9} />
            <Bar dataKey="Library" name="Library & Health Centre" stackId="a" fill="#85ba8a" maxBarSize={9} />
            <Bar dataKey="Morrisons" name="Morrisons" stackId="a" fill="#8884d8" maxBarSize={9} />
            <Bar dataKey="PoundWorld" name="Pound World" stackId="a" fill="#f2ca9d" maxBarSize={9} />
        </BarChart>
    </>
);

const VehicleOccupancyForSelectedTime = ({ data }) => (
    <>
        <h5 className="trchartheader">Vehicle Occupancy For Selected Time</h5>
        <PieChart width={380} height={360}>
            <Pie
                data={data}
                outerRadius={120}
                cx={150}
                cy={180}
                fill="#8884d8"
                nameKey="name"
                dataKey="value"
                label={renderPieLabel}
            >
                {data.map((entry, index) => (
                    <Cell key={`vehocccell${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
            </Pie>
            <Legend layout="vertical" verticalAlign="top" align="right" />
        </PieChart>
    </>
);

export const ModeSplitForSelectedTime = ({ data }) => (
    <>
        <h5 className="trchartheader">Mode Split For Selected Time</h5>
        <div style={{ width: "100%", height: 400 }}>
            <ResponsiveContainer>
                <PieChart> 
                    <Pie
                        data={data}
                        outerRadius={120}
                        cx={150}
                        cy={180}
                        fill="#8884d8"
                        nameKey="name"
                        dataKey="value"
                        label={renderPieLabel}
                    >
                        {data.map((entry, index) => (
                            <Cell key={`modesplitcell${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Legend layout="vertical" verticalAlign="top" align="right" />
                </PieChart>
            </ResponsiveContainer>
        </div>
    </>
);

const ModeChoiceFlowProfile = ({ data }) => (
    <>
        <h5 className="trchartheader">Mode Choice Flow Profile</h5>
        <div style={{ width: "100%", height: 460 }}>
            <ResponsiveContainer>
                <LineChart data={data}
                    margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="HalfHour" height={60} interval={0} tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 13 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="CARS" name="CAR" stroke={COLORS[0]} strokeWidth={2} />
                    <Line type="monotone" dataKey="PEDNS" name="PED" stroke={COLORS[1]} strokeWidth={2} />
                    <Line type="monotone" dataKey="PEDCYC" name="CYCLE" stroke={COLORS[2]} strokeWidth={2} />
                    <Line type="monotone" dataKey="TAXIS" name="TAXI" stroke={COLORS[3]} strokeWidth={2} />
                    <Line type="monotone" dataKey="MOTCYC" name="MOTORBIKE" stroke={COLORS[4]} strokeWidth={2} />
                    <Line type="monotone" dataKey="BUSPAS" name="BUS PAS" stroke={COLORS[5]} strokeWidth={2} />
                    <Line type="monotone" dataKey="LGVS" name="LGVS" stroke={COLORS[6]} strokeWidth={2} />
                    <Line type="monotone" dataKey="OGVS" name="OGVS" stroke={COLORS[7]} strokeWidth={2} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    </>
);

const PortalGuide = () => {
    const navigate = useNavigate();

    return (
        <>
            <Grid size={12}><h4>CAMBOURNE VILLAGE SURVEY</h4></Grid>
            <Grid size={12}>
                Copyright &copy;2020 TRICS Consortium Ltd
                <br />
            </Grid>
            <Grid size={12}>
                An analysis of a number of surveys that we undertook on Thursday 7th June 2018 in Cambourne, Cambridgeshire.
            </Grid>
            <Grid size={12}>
                <Button
                    fullWidth
                    size="small"
                    title="Go back to the TRICS Members Area page"
                    onClick={() => navigate('/home')} // use a leading slash for absolute paths
                >
                    Return to the TRICS Members Area
                </Button>
            </Grid>
        </>
    );
};


function ToggleButtonGroupControlled({ value, setValue }: { value: number[], setValue: (val: number[]) => void }) {
    return (
        <>
            <h6 className="trfilterheader">Development</h6>
            <ToggleButtonGroup
                orientation="vertical"
                value={value}
                onChange={(_, val) => setValue(val)}
                size="small"
            >
                <ToggleButton value={0} size="small">Church</ToggleButton>
                <ToggleButton value={1} size="small">Community Centre</ToggleButton>
                <ToggleButton value={2} size="small">Fitness Centre</ToggleButton>
                <ToggleButton value={3} size="small">Just for Pets</ToggleButton>
                <ToggleButton value={4} size="small">Library & Health Centre</ToggleButton>
                <ToggleButton value={5} size="small">Morrisons</ToggleButton>
                <ToggleButton value={6} size="small">Pound World</ToggleButton>
            </ToggleButtonGroup>
        </>
    );
}

function UserButtonGroupControlled({ value, setValue }: { value: number[], setValue: (val: number[]) => void }) {
    return (
        <>
            <h6>User</h6>
            <ToggleButtonGroup
                orientation="vertical"
                value={value}
                onChange={(_, val) => setValue(val)}
                size="small"
            >
                <ToggleButton value={0} size="small">Live</ToggleButton>
                <ToggleButton value={1} size="small">Servicing</ToggleButton>
                <ToggleButton value={2} size="small">Visitor</ToggleButton>
                <ToggleButton value={3} size="small">Work</ToggleButton>
                <ToggleButton value={4} size="small">Work-Live</ToggleButton>
            </ToggleButtonGroup>
        </>
    );
}

function DirnButtonGroupControlled({ value, setValue }: { value: number[], setValue: (val: number[]) => void }) {
    return (
        <>
            <h6 className="trfilterheader">Direction</h6>
            <ToggleButtonGroup
                orientation="vertical"
                value={value}
                onChange={(_, val) => setValue(val)}
                size="small"
            >
                <ToggleButton value={0} size="small">In</ToggleButton>
                <ToggleButton value={1} size="small">Out</ToggleButton>
            </ToggleButtonGroup>
        </>
    );
}

function OrigButtonGroupControlled({ value, setValue }: { value: number[], setValue: (val: number[]) => void }) {
    return (
        <>
            <h6>Origin</h6>
            <ToggleButtonGroup
                orientation="vertical"
                value={value}
                onChange={(_, val) => setValue(val)}
                size="small"
            >
                <ToggleButton value={0} size="small">External</ToggleButton>
                <ToggleButton value={1} size="small">Internal</ToggleButton>
            </ToggleButtonGroup>
        </>
    );
}

function TimeButtonGroupControlled({ value, setValue }: { value: number[], setValue: (val: number[]) => void }) {
    // If you want a scrollable list:
    return (
        <>
            <h6>Time</h6>
            <Box sx={{ overflowY: 'auto', height: 490 }}>
                <ToggleButtonGroup
                    orientation="vertical"
                    value={value}
                    onChange={(_, val) => setValue(val)}
                    size="small"
                >
                    <ToggleButton value={0} size="small">0700-0730</ToggleButton>
                    <ToggleButton value={1} size="small">0730-0800</ToggleButton>
                    <ToggleButton value={2} size="small">0800-0830</ToggleButton>
                    <ToggleButton value={3} size="small">0830-0900</ToggleButton>
                    <ToggleButton value={4} size="small">0900-0930</ToggleButton>
                    <ToggleButton value={5} size="small">0930-1000</ToggleButton>
                    <ToggleButton value={6} size="small">1000-1030</ToggleButton>
                    <ToggleButton value={7} size="small">1030-1100</ToggleButton>
                    <ToggleButton value={8} size="small">1100-1130</ToggleButton>
                    <ToggleButton value={9} size="small">1130-1200</ToggleButton>
                    <ToggleButton value={10} size="small">1200-1230</ToggleButton>
                    <ToggleButton value={11} size="small">1230-1300</ToggleButton>
                    <ToggleButton value={12} size="small">1300-1330</ToggleButton>
                    <ToggleButton value={13} size="small">1330-1400</ToggleButton>
                    <ToggleButton value={14} size="small">1400-1430</ToggleButton>
                    <ToggleButton value={15} size="small">1430-1500</ToggleButton>
                    <ToggleButton value={16} size="small">1500-1530</ToggleButton>
                    <ToggleButton value={17} size="small">1530-1600</ToggleButton>
                    <ToggleButton value={18} size="small">1600-1630</ToggleButton>
                    <ToggleButton value={19} size="small">1630-1700</ToggleButton>
                    <ToggleButton value={20} size="small">1700-1730</ToggleButton>
                    <ToggleButton value={21} size="small">1730-1800</ToggleButton>
                    <ToggleButton value={22} size="small">1800-1830</ToggleButton>
                    <ToggleButton value={23} size="small">1830-1900</ToggleButton>
                </ToggleButtonGroup>
            </Box>
        </>
    );
}





// ...Do similar for UserButtonGroupControlled, DirnButtonGroupControlled, OrigButtonGroupControlled, TimeButtonGroupControlled

// --- MAIN COMPONENT ---
const Cambourne = () => {
    const [devToggVal, setDevToggVal] = useState(DevInitToggVal);
    const [userToggVal, setUserToggVal] = useState(UserInitToggVal);
    const [dirnToggVal, setDirnToggVal] = useState(DirnInitToggVal);
    const [origToggVal, setOrigToggVal] = useState(OrigInitToggVal);
    const [timeToggVal, setTimeToggVal] = useState(TimeInitToggVal);

    const [personUserChart, setPersonUserChart] = useState<PersonUserdata[]>([]);
    const [personDevelChart, setPersonDevelChart] = useState<PersonDeveldata[]>([]);
    const [modeChoiceChart, setModeChoiceChart] = useState<ModeChoicedata[]>([]);
    const [vehOccupPieChart, setVehOccupPieChart] = useState<ChartData[]>([]);
    const [modeSplitPieChart, setModeSplitPieChart] = useState<ChartData[]>([]);

    useEffect(() => {
        const {
            personUser,
            personDevel,
            modeChoice,
            vehOccupPie,
            modeSplitPie
        } = parseTricsData(
            trdatacsv as tricsdata[],
            devToggVal,
            userToggVal,
            dirnToggVal,
            origToggVal,
            timeToggVal
        );
        setPersonUserChart(personUser);
        setPersonDevelChart(personDevel);
        setModeChoiceChart(modeChoice);
        setVehOccupPieChart(vehOccupPie);
        setModeSplitPieChart(modeSplitPie);
    }, [devToggVal, userToggVal, dirnToggVal, origToggVal, timeToggVal]);


    return (
        <Grid container sx={{ alignItems: "flex-start" }}>
            {/*<Grid>*/}
            {/*    <HeaderTitle />*/}
            {/*</Grid>*/}
            <Grid size={3} container justifyItems="flex-start">
                <Grid size={12}>
                    <PortalGuide />
                </Grid>
                <Grid container size={6}>
                    <Grid size={12}>
                        <ToggleButtonGroupControlled value={devToggVal} setValue={setDevToggVal} />
                    </Grid>
                    <Grid size={12}>
                        <UserButtonGroupControlled value={userToggVal} setValue={setUserToggVal} />
                    </Grid>
                    <Grid size={12}>
                        <OrigButtonGroupControlled value={origToggVal} setValue={setOrigToggVal} />
                    </Grid>
                </Grid>
                <Grid container size={6}>
                    <Grid size={12}>
                        <TimeButtonGroupControlled value={timeToggVal} setValue={setTimeToggVal} />
                    </Grid>
                    <Grid size={12}>
                        <DirnButtonGroupControlled value={dirnToggVal} setValue={setDirnToggVal} />
                    </Grid>
                </Grid>
            </Grid>
            <Grid container size={4.5}>
                <ModeChoiceFlowProfile data={modeChoiceChart} />
                <Grid size={6}>
                    <ModeSplitForSelectedTime data={modeSplitPieChart} />
                </Grid>
                <Grid size={6}>
                    <VehicleOccupancyForSelectedTime data={vehOccupPieChart} />
                </Grid>
            </Grid>

            <Grid size={4.5} container>
                <Grid size={12}>
                    <PersonTripsPerDevelopment data={personDevelChart} />
                </Grid>
                <Grid size={12}>
                    <PersonByUserChart data={personUserChart} />
                </Grid>
                {/*<Grid size={12}>*/}
                {/*    <PortalNotes />*/}
                {/*</Grid>*/}
            </Grid>
        </Grid>
    );
};

export default Cambourne;
